import { h } from 'preact'

import type { QaTriColor } from './types'
import type { QaStrings } from './i18n'

/**
 * A row of three independent lane chips for a scenario (or a suite aggregate):
 *  - dev (`code`)   = developer validation (the `</>` chevrons);
 *  - bot (`bot`)    = automated execution (Playwright);
 *  - client (`human`) = human/client validation.
 *
 * Each chip carries its OWN tri-color (green/yellow/gray) — they are independent:
 * a human may have validated while the bot never ran, etc. Pure presentational:
 * no hooks, no state, no fetching.
 *
 * SVG path data copied verbatim from the donor `qa-lane-icons.vue`.
 */
export interface LaneIconsProps {
  dev: QaTriColor
  bot: QaTriColor
  human: QaTriColor
  strings: QaStrings
}

/** Tri-color → BEM modifier (donor mapping: `qa--green|qa--yellow|qa--gray`). */
const TRI_MODIFIER: Record<QaTriColor, string> = {
  green: 'qa--green',
  yellow: 'qa--yellow',
  gray: 'qa--gray',
}

export function LaneIcons({ dev, bot, human, strings }: LaneIconsProps) {
  return h(
    'span',
    { class: 'qa-lanes', 'data-testid': 'qa-lane-icons' },
    // dev — chevrons </>
    h(
      'span',
      {
        class: `qa-lanes__icon qa-lane ${TRI_MODIFIER[dev]}`,
        title: strings['lane.code'],
        'aria-label': strings['lane.code'],
        'data-testid': 'qa-lane-dev',
        'data-state': dev,
      },
      h(
        'svg',
        { class: 'qa-lanes__svg', viewBox: '0 0 24 24', 'aria-hidden': 'true' },
        h('path', {
          d: 'M8 7l-5 5 5 5M16 7l5 5-5 5',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': '2.2',
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
        }),
      ),
    ),
    // bot — robot head
    h(
      'span',
      {
        class: `qa-lanes__icon qa-lane ${TRI_MODIFIER[bot]}`,
        title: strings['lane.bot'],
        'aria-label': strings['lane.bot'],
        'data-testid': 'qa-lane-bot',
        'data-state': bot,
      },
      h(
        'svg',
        {
          class: 'qa-lanes__svg',
          viewBox: '0 0 24 24',
          'aria-hidden': 'true',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': '2',
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
        },
        h('rect', { x: '4.5', y: '8.5', width: '15', height: '10.5', rx: '2.5' }),
        h('path', { d: 'M12 4.5v4' }),
        h('circle', { cx: '12', cy: '3.4', r: '1.3', fill: 'currentColor', stroke: 'none' }),
        h('path', { d: 'M2.5 12.5v3M21.5 12.5v3' }),
        h('circle', { cx: '9', cy: '13.5', r: '1.2', fill: 'currentColor', stroke: 'none' }),
        h('circle', { cx: '15', cy: '13.5', r: '1.2', fill: 'currentColor', stroke: 'none' }),
      ),
    ),
    // client — person
    h(
      'span',
      {
        class: `qa-lanes__icon qa-lane ${TRI_MODIFIER[human]}`,
        title: strings['lane.human'],
        'aria-label': strings['lane.human'],
        'data-testid': 'qa-lane-client',
        'data-state': human,
      },
      h(
        'svg',
        {
          class: 'qa-lanes__svg',
          viewBox: '0 0 24 24',
          'aria-hidden': 'true',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': '2',
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
        },
        h('circle', { cx: '12', cy: '8', r: '3.6' }),
        h('path', { d: 'M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6' }),
      ),
    ),
  )
}
